import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle2, PartyPopper, CalendarDays, MessageCircle, ChevronLeft, RotateCcw, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AccessInstructionsView } from "@/components/owner/AccessInstructionsView";
import CleaningTab from "@/components/cleaner/CleaningTab";
import { EntryWalkthroughStep } from "@/components/cleaner/EntryWalkthroughStep";
import { PreVisitStepHeader } from "@/components/cleaner/PreVisitStepHeader";
import InventoryCheckTab from "@/components/cleaner/InventoryCheckTab";
import { SimpleInventoryStep } from "@/components/cleaner/SimpleInventoryStep";
import { GuestAmenitySetupStep, buildLeftOutRecallFromStaging } from "@/components/cleaner/GuestAmenitySetupStep";
import { parseGuestSetupState } from "@/types/lifecycle";
import CleaningTimer from "@/components/cleaner/CleaningTimer";
import { TaskPreStartView } from "@/components/cleaner/TaskPreStartView";
import CheckoutTimeBanner from "@/components/cleaner/CheckoutTimeBanner";
import FloatingChecklistHeader from "@/components/cleaner/FloatingChecklistHeader";
import { TurnoverUnitIssueReportDialog } from "@/components/cleaner/TurnoverUnitIssueReportDialog";
import { RoomAwareStep } from "@/components/cleaner/RoomAwareStep";
import { SimpleCleanStep } from "@/components/cleaner/SimpleCleanStep";
import { SimpleKeysStep } from "@/components/cleaner/SimpleKeysStep";
import { SimpleDropKeysStep } from "@/components/cleaner/SimpleDropKeysStep";
import { SimpleDoneStep } from "@/components/cleaner/SimpleDoneStep";
import { SimpleMaintenanceDueStep } from "@/components/cleaner/SimpleMaintenanceDueStep";
import { SimpleFlowProgress } from "@/components/cleaner/SimpleFlowProgress";
import {
  TurnoverStepStrip,
  getTurnoverFlowSummary,
  resolveTurnoverPhase,
} from "@/components/cleaner/TurnoverStepStrip";
import { TurnoverWizard } from "@/components/cleaner/TurnoverWizard";
import {
  buildTurnoverWizardSteps,
  getActiveWizardStepId,
  simpleFlowSegmentIndex,
  type TurnoverWizardContext,
} from "@/lib/turnoverWizardPlan";
import {
  buildSimpleFlowRewind,
  collectSimplePhotoUrls,
  isSimpleDropKeysComplete,
  isSimpleTurnoverFullyDone,
  previousSimpleSegmentIndex,
  readSimpleFlowState,
  simpleFlowSegments,
  simpleSegmentIdFromIndex,
  SIMPLE_TURNOVER_V1,
} from "@/lib/simpleTurnover";
import { SimpleQuickCheckStep } from "@/components/cleaner/SimpleQuickCheckStep";
import { supabase } from "@/integrations/supabase/client";
import { useElapsedSinceLabel } from "@/hooks/useElapsedSince";
import { useCleanerAssignment, usePropertyForCleaner, useCleanerRecord, useInventoryForCleaner, useMarkTaskComplete } from "@/hooks/useCleaner";
import { setActiveJobClock } from "@/lib/activeJobClock";
import { useCurrentCleaningTask, useStartCleaningTimer, type StartLocationSnapshot } from "@/hooks/useCurrentCleaningTask";
import { useUpcomingCleaningTasks } from "@/hooks/useUpcomingCleaningTasks";
import { useCleaningProgress } from "@/hooks/useCleaningProgress";
import { computeEffectiveTimes } from "@/lib/effectiveTimes";
import { formatScheduledDate } from "@/lib/dateUtils";
import { canStartTurnoverClean, formatTurnoverOpensHint } from "@/lib/turnoverWindow";
import { useRefreshTurnoverWindow } from "@/hooks/useRefreshTurnoverWindow";
import { useWalkQuickChecks } from "@/hooks/useWalkQuickChecks";
import { useDueCleanerMaintenanceChecks } from "@/hooks/useDueMaintenanceChecks";
import { TURNOVER_RUNNER_NAVBAR_CLASSNAME } from "@/lib/turnoverChrome";
import {
  clearCleanerResumeCleanUrl,
  clearCleanerTurnoverSessionForTask,
  clearPrevisitWizardDraft,
  readPrevisitWizardDraft,
  setCleanerResumeCleanUrl,
  writePrevisitWizardDraft,
} from "@/lib/cleanerVisitPersistence";
import { cn } from "@/lib/utils";
import { scrollRootToTop } from "@/lib/scrollRootToTop";
import { MessageThreadModal } from "@/components/messaging/MessageThreadModal";
import { AppLayout } from "@/components/layout/AppLayout";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/use-toast";
import { usePendingPickupReorders } from "@/hooks/useInventoryReorders";

// Cleaning flow state machine
type CleaningFlowState = "checklist" | "inventory" | "complete";

const CleanerPortal = () => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const taskIdFromUrl = searchParams.get("taskId");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, profile, loading: authLoading } = useAuth();
  const { t, tDynamic } = useTranslation();
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unitIssueReportOpen, setUnitIssueReportOpen] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [preVisitStep, setPreVisitStep] = useState(0);
  const [preVisitLocation, setPreVisitLocation] = useState<StartLocationSnapshot | null>(null);
  const lastPrevisitHydratedKeyRef = useRef("");

  // Floating header state
  const [floatingHeaderVisible, setFloatingHeaderVisible] = useState(false);
  const [progressData, setProgressData] = useState({ completedCount: 0, totalTasks: 0, isSaving: false });
  const [displayChecklistName, setDisplayChecklistName] = useState("");
  const mainHeaderRef = useRef<HTMLDivElement>(null);

  // Success banner state
  const [showChecklistSuccess, setShowChecklistSuccess] = useState(false);

  // Active tab (controlled so we can switch to inventory when checklist is completed)
  const [activeTab, setActiveTab] = useState<"cleaning" | "inventory">("cleaning");

  // Cleaning flow state machine - initialized once per task, never reset on re-render
  const [cleaningFlowState, setCleaningFlowState] = useState<CleaningFlowState>("checklist");
  const stateInitializedRef = useRef<string | false>(false);

  const { data: cleaner, isLoading: cleanerLoading } = useCleanerRecord();
  const { data: currentTask, isLoading: taskLoading, isError: taskError } = useCurrentCleaningTask(propertyId, taskIdFromUrl);
  /** Task row is source of truth; URL :propertyId can be stale (resume link, bookmark). */
  const effectivePropertyId = currentTask?.property_id ?? propertyId;
  useRefreshTurnoverWindow({
    propertyId: effectivePropertyId,
    taskId: currentTask?.id ?? taskIdFromUrl,
    enabled: Boolean(effectivePropertyId && (currentTask?.id || taskIdFromUrl)),
  });
  const { data: assignment, isLoading: assignmentLoading } = useCleanerAssignment(effectivePropertyId);
  const { data: property, isLoading: propertyLoading } = usePropertyForCleaner(effectivePropertyId);
  const { data: walkQuickChecks = [] } = useWalkQuickChecks(effectivePropertyId);
  const { due: dueMaintChecks } = useDueCleanerMaintenanceChecks(
    effectivePropertyId,
    currentTask?.scheduled_date,
  );
  const includeMaintenance = dueMaintChecks.length > 0;
  const includeQuickCheck = walkQuickChecks.length > 0;
  const guestStagingLineCount = currentTask?.checklist_guest_amenity_staging?.length ?? 0;
  const includeGuestSetup = guestStagingLineCount > 0;
  const flowChrome = useMemo(
    () => ({ includeMaintenance, includeQuickCheck, includeGuestSetup }),
    [includeMaintenance, includeQuickCheck, includeGuestSetup],
  );
  const { data: upcomingTasks } = useUpcomingCleaningTasks(currentTask?.id);
  const { data: inventoryItems } = useInventoryForCleaner(effectivePropertyId);
  const { data: pendingPickupReorders = [] } = usePendingPickupReorders(effectivePropertyId);
  const startCleaningMutation = useStartCleaningTimer();
  const markTaskCompleteMutation = useMarkTaskComplete();

  // Slice 49: running job clock shown above the step strip.
  useEffect(() => {
    const j = currentTask;
    if (j && j.status === "in_progress" && j.started_at) {
      setActiveJobClock({
        startedAt: j.started_at,
        locationNotSaved: j.start_location_lat == null && !!j.start_location_override_reason,
      });
    } else {
      setActiveJobClock(null);
    }
    return () => setActiveJobClock(null);
  }, [currentTask?.id, currentTask?.status, currentTask?.started_at, currentTask?.start_location_lat, currentTask?.start_location_override_reason]);
  const { resetProgress } = useCleaningProgress(currentTask?.id);

  const showScheduledTaskIntro = Boolean(
    currentTask &&
      taskIdFromUrl &&
      taskIdFromUrl === currentTask.id &&
      currentTask.status === "scheduled"
  );

  const showPreVisitWizard = Boolean(
    !SIMPLE_TURNOVER_V1 &&
      currentTask &&
      taskIdFromUrl &&
      taskIdFromUrl === currentTask.id &&
      currentTask.status === "in_progress" &&
      !currentTask.entry_walkthrough_completed &&
      cleaner?.id
  );

  const preVisitElapsedLabel = useElapsedSinceLabel(
    showPreVisitWizard && currentTask?.started_at ? currentTask.started_at : undefined
  );

  // When cleaner has a task for this property, don't block on assignment so portal can load.
  // When taskId is in URL, wait for task query so we don't show "Not Assigned" or wrong content briefly.
  const isLoading =
    authLoading ||
    cleanerLoading ||
    (propertyLoading && !currentTask) ||
    (assignmentLoading && !currentTask) ||
    (!!taskIdFromUrl && taskLoading);

  // Team members and owners should use the task runner at /tasks/run/, not the cleaner portal. Redirect so they can complete the task.
  useEffect(() => {
    if (authLoading || !user || !profile || profile.role === "cleaner" || !taskIdFromUrl) return;
    const runPropertyId = currentTask?.property_id ?? propertyId;
    if (!runPropertyId) return;
    navigate(`/tasks/run/${runPropertyId}?taskId=${taskIdFromUrl}`, { replace: true });
  }, [authLoading, user, profile?.role, propertyId, currentTask?.property_id, taskIdFromUrl, navigate]);

  // Keep /clean/:propertyId in sync with the task's property (fixes wrong property name on resume / bad links).
  useEffect(() => {
    if (!taskIdFromUrl || !currentTask?.property_id || !propertyId) return;
    if (currentTask.id !== taskIdFromUrl) return;
    if (currentTask.property_id === effectivePropertyId) return;
    const params = new URLSearchParams(searchParams);
    navigate(`/clean/${currentTask.property_id}?${params.toString()}`, { replace: true });
  }, [taskIdFromUrl, currentTask?.id, currentTask?.property_id, propertyId, navigate, searchParams]);

  // Sync cleaning flow state from currentTask so progress persists when navigating away and back
  // Use primitive deps only to avoid re-running when React Query returns new object references (prevents update loop #185)
  const taskId = currentTask?.id;
  const checklistDone = !!currentTask?.checklist_completed;
  const inventoryDone = !!currentTask?.inventory_completed;
  const hasInventoryItems = !!(inventoryItems && inventoryItems.length > 0) && (currentTask?.include_inventory_check !== false);
  const dropKeysDone = isSimpleDropKeysComplete(readSimpleFlowState(currentTask?.entry_walkthrough_state));
  const taskStatusCompleted = currentTask?.status === "completed";
  const simpleFullyDone = isSimpleTurnoverFullyDone({
    entryWalkthroughState: currentTask?.entry_walkthrough_state,
    checklistCompleted: currentTask?.checklist_completed,
    inventoryCompleted: currentTask?.inventory_completed,
    includeInventoryCheck: currentTask?.include_inventory_check,
    hasInventoryItems,
    taskStatus: currentTask?.status,
  });

  useEffect(() => {
    if (!taskId) return;

    const signature = `${taskId}-${checklistDone}-${inventoryDone}-${dropKeysDone}-${taskStatusCompleted}-${SIMPLE_TURNOVER_V1}`;
    if (stateInitializedRef.current === signature) return;

    setCleaningFlowState((prev) => {
      if (SIMPLE_TURNOVER_V1) {
        // After photos set checklist_completed — that must NOT finish the job.
        // Only drop keys (or DB status=completed) may advance to local "complete".
        if (simpleFullyDone || taskStatusCompleted) return "complete";
        if (prev === "complete") {
          // Recover stuck sessions that flipped complete before drop keys
          if (checklistDone && hasInventoryItems && !inventoryDone) return "inventory";
          return "checklist";
        }
        if (checklistDone && hasInventoryItems && !inventoryDone) return "inventory";
        return "checklist";
      }

      // Legacy flow
      if (prev === "complete") {
        if (checklistDone && inventoryDone) return "complete";
        return prev;
      }
      if (checklistDone && inventoryDone) return "complete";
      if (checklistDone) return hasInventoryItems ? "inventory" : "complete";
      return "checklist";
    });

    stateInitializedRef.current = signature;
  }, [
    taskId,
    checklistDone,
    inventoryDone,
    hasInventoryItems,
    dropKeysDone,
    taskStatusCompleted,
    simpleFullyDone,
  ]);

  const nextTask = upcomingTasks?.[0];

  // IntersectionObserver to detect main header visibility for floating header
  // Use currentTask?.id only so we don't re-run when React Query returns a new object reference (prevents #185 loop)
  useEffect(() => {
    if (!mainHeaderRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        setFloatingHeaderVisible((prev) => (prev === visible ? prev : visible));
      },
      {
        threshold: 0,
        rootMargin: "-60px 0px 0px 0px", // Account for navbar
      }
    );

    observer.observe(mainHeaderRef.current);

    return () => observer.disconnect();
  }, [currentTask?.id]); // Re-attach when task id changes only

  useEffect(() => {
    if (!currentTask?.id) return;
    if (currentTask.entry_walkthrough_completed) {
      clearPrevisitWizardDraft(currentTask.id);
    }
  }, [currentTask?.id, currentTask?.entry_walkthrough_completed]);

  useEffect(() => {
    if (!showPreVisitWizard || !currentTask?.id) {
      lastPrevisitHydratedKeyRef.current = "";
      return;
    }
    const key = currentTask.id;
    if (lastPrevisitHydratedKeyRef.current === key) return;
    lastPrevisitHydratedKeyRef.current = key;
    const draft = readPrevisitWizardDraft(currentTask.id);
    if (draft) {
      setPreVisitStep(draft.step);
      setPreVisitLocation(draft.preVisitLocation ?? null);
    } else {
      setPreVisitStep(0);
      setPreVisitLocation(null);
    }
  }, [showPreVisitWizard, currentTask?.id]);

  useEffect(() => {
    if (!showPreVisitWizard || !currentTask?.id) return;
    writePrevisitWizardDraft(currentTask.id, {
      v: 1,
      step: Math.min(2, Math.max(0, preVisitStep)) as 0 | 1 | 2,
      preVisitLocation: preVisitLocation ?? undefined,
    });
  }, [showPreVisitWizard, currentTask?.id, preVisitStep, preVisitLocation]);

  useEffect(() => {
    if (!showPreVisitWizard) return;
    scrollRootToTop();
  }, [preVisitStep, showPreVisitWizard]);

  useEffect(() => {
    if (!currentTask?.property_id || !taskIdFromUrl || !currentTask?.id || currentTask.id !== taskIdFromUrl) return;
    if (currentTask.status === "in_progress" && cleaningFlowState !== "complete") {
      setCleanerResumeCleanUrl(`/clean/${currentTask.property_id}?taskId=${currentTask.id}`);
    }
  }, [currentTask?.property_id, taskIdFromUrl, currentTask?.id, currentTask?.status, cleaningFlowState]);

  useEffect(() => {
    if (cleaningFlowState === "complete" && currentTask?.id && taskIdFromUrl === currentTask.id) {
      clearCleanerResumeCleanUrl();
    }
  }, [cleaningFlowState, currentTask?.id, taskIdFromUrl]);

  useEffect(() => {
    if (!taskIdFromUrl || taskLoading) return;
    if (!currentTask) {
      clearCleanerTurnoverSessionForTask(taskIdFromUrl);
    }
  }, [taskIdFromUrl, taskLoading, currentTask]);

  // When checklist is completed and we have inventory, switch to inventory tab. Guard to avoid repeated setState (prevents React #185 update loop)
  useEffect(() => {
    if (cleaningFlowState === "inventory") {
      setActiveTab((prev) => (prev === "inventory" ? prev : "inventory"));
    } else {
      setActiveTab((prev) => (prev === "cleaning" ? prev : "cleaning"));
    }
  }, [cleaningFlowState]);

  useEffect(() => {
    if (!currentTask?.id || !taskIdFromUrl || currentTask.id !== taskIdFromUrl) return;
    scrollRootToTop();
  }, [activeTab, currentTask?.id, taskIdFromUrl]);

  // Handle restart cleaning
  const handleRestartCleaning = () => {
    resetProgress();
    setProgressData({ completedCount: 0, totalTasks: progressData.totalTasks, isSaving: false });
    setShowRestartConfirm(false);
    queryClient.invalidateQueries({ queryKey: ["cleaning-progress", currentTask?.id] });
  };

  // Find the next task for THIS SPECIFIC property (not already loaded as currentTask)
  const nextTaskForThisProperty = upcomingTasks?.find(t => t.property_id === effectivePropertyId) || null;

  const handleStartTask = async (taskId: string, scheduledDate: string) => {
    if (!canStartTurnoverClean(scheduledDate)) {
      toast({
        title: t("cleaningStartNotScheduledDayTitle"),
        description: t("cleaningStartNotScheduledDay"),
        variant: "default",
      });
      return;
    }
    try {
      await startCleaningMutation.mutateAsync({
        taskId,
        propertyId: effectivePropertyId!,
        enforceScheduledDay: true,
      });
    } catch (error) {
      console.error("[CleanerPortal] Failed to start task:", error);
      const msg = error instanceof Error ? error.message : "";
      if (msg === "CLEANING_START_NOT_SCHEDULED_DAY") {
        toast({
          title: t("cleaningStartNotScheduledDayTitle"),
          description: t("cleaningStartNotScheduledDay"),
          variant: "default",
        });
      } else if (msg === "CLEANING_START_NOT_ASSIGNED_CLEANER") {
        toast({
          title: t("cleaningStartNotAssignedTitle"),
          description: t("cleaningStartNotAssigned"),
          variant: "default",
        });
      } else {
        toast({
          title: t("error"),
          description: t("failedToStartTimer"),
          variant: "destructive",
        });
      }
    }
  };

  // State machine callbacks - advance state only after successful mutations
  // Uses actual inventory existence, NOT completion flags
  const handleChecklistSubmitted = async () => {
    // GUARD: Prevent double submission - if already past checklist state, ignore
    if (cleaningFlowState !== "checklist") {
      console.warn("[CleanerPortal] Checklist submission ignored - already past checklist state");
      return;
    }

    // Show success banner
    setShowChecklistSuccess(true);
    
    // Hide banner after 5 seconds
    setTimeout(() => {
      setShowChecklistSuccess(false);
    }, 5000);
    
    const hasInventory = (inventoryItems && inventoryItems.length > 0) && (currentTask?.include_inventory_check !== false);
    if (!hasInventory && currentTask?.id) {
      try {
        await markTaskCompleteMutation.mutateAsync({
          taskId: currentTask.id,
          propertyId: effectivePropertyId!,
        });
      } catch (e) {
        console.error("[CleanerPortal] markTaskComplete failed:", e);
      }
    }
    setCleaningFlowState(hasInventory ? "inventory" : "complete");
    queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
    queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
    queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
    queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
  };

  const handleInventorySubmitted = () => {
    // GUARD: Prevent inventory submission before checklist completion
    // If state is still "checklist", inventory cannot be submitted
    if (cleaningFlowState === "checklist") {
      console.warn("[CleanerPortal] Inventory submission blocked - checklist not completed");
      toast({ 
        title: t("error"), 
        description: t("completeChecklistFirst"), 
        variant: "destructive" 
      });
      return;
    }

    // GUARD: Prevent double submission - if already complete, ignore
    if (cleaningFlowState === "complete") {
      console.warn("[CleanerPortal] Inventory submission ignored - already complete");
      return;
    }

    // Advance state machine FIRST (before refetch)
    setCleaningFlowState(prev => {
      // Advance from "inventory" to complete
      if (prev === "inventory") {
        // Task is already marked as completed by useSubmitInventory hook
        return "complete";
      }
      // Edge case: inventory submitted but state was still "checklist" (shouldn't happen with guard above)
      if (prev === "checklist" && currentTask?.checklist_completed) {
        return "complete";
      }
      return prev;
    });

    // RACE CONDITION FIX: Refetch queries AFTER state machine advances
    setTimeout(() => {
      queryClient.refetchQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
      queryClient.refetchQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
      queryClient.refetchQueries({ queryKey: ["in-progress-cleaning-tasks"] });
    }, 100); // Small delay to ensure state update completes
  };

  // Derived state from state machine (replaces inferred logic)
  const checklistCompleted = cleaningFlowState === "inventory" || cleaningFlowState === "complete";
  const inventoryCompleted = cleaningFlowState === "complete";
  // SIMPLE: never treat After/Inventory flags alone as finished — drop keys must run (and usually status=completed).
  const bothCompleted = SIMPLE_TURNOVER_V1
    ? cleaningFlowState === "complete" && simpleFullyDone
    : cleaningFlowState === "complete";

  const showEntryWalkthrough =
    !!currentTask &&
    currentTask.status === "in_progress" &&
    !currentTask.entry_walkthrough_completed &&
    !!cleaner?.id;

  /** Must run before any early return — same hook order on loading / pre-start / main portal (React #310). */
  const showBothTabs =
    (cleaningFlowState === "checklist" || cleaningFlowState === "inventory") &&
    !!inventoryItems &&
    inventoryItems.length > 0 &&
    currentTask?.include_inventory_check !== false;

  const turnoverPhase = useMemo(
    () =>
      resolveTurnoverPhase({
        showEntryWalkthrough,
        cleaningFlowState,
        inventoryGuestPhaseCompleted: currentTask?.inventory_guest_phase_completed,
        hasSplitPostCleanInventory: showBothTabs,
      }),
    [
      showEntryWalkthrough,
      cleaningFlowState,
      currentTask?.inventory_guest_phase_completed,
      showBothTabs,
    ]
  );

  const showEntryColumn = Boolean(
    currentTask?.status === "in_progress" && cleaner?.id && !bothCompleted
  );

  const flowSummaryShort = useMemo(
    () => getTurnoverFlowSummary(turnoverPhase, showEntryColumn, false, tDynamic),
    [turnoverPhase, showEntryColumn, showBothTabs, tDynamic]
  );

  const turnoverInventoryMeta = useMemo(() => {
    const items = (inventoryItems ?? []) as { inventory_scope?: string | null }[];
    const post = items.filter((i) => i.inventory_scope !== "unit");
    const guest = post.filter((i) => !i.inventory_scope || i.inventory_scope === "guest_amenities").length;
    const supply = post.filter((i) => i.inventory_scope === "supply_closet").length;
    return { postCleanCount: post.length, guestAmenitiesCount: guest, supplyClosetCount: supply };
  }, [inventoryItems]);

  const turnoverWizardContext = useMemo((): TurnoverWizardContext | null => {
    if (!currentTask || currentTask.status !== "in_progress") return null;
    return {
      status: currentTask.status,
      startedAt: currentTask.started_at,
      entryWalkthroughCompleted: currentTask.entry_walkthrough_completed,
      checklistCompleted: currentTask.checklist_completed,
      inventoryCompleted: currentTask.inventory_completed,
      inventoryGuestPhaseCompleted: currentTask.inventory_guest_phase_completed,
      inventorySupplyPhaseCompleted: currentTask.inventory_supply_phase_completed,
      includeInventoryCheck: currentTask.include_inventory_check,
      postCleanInventoryCount: turnoverInventoryMeta.postCleanCount,
      guestAmenitiesCount: turnoverInventoryMeta.guestAmenitiesCount,
      supplyClosetCount: turnoverInventoryMeta.supplyClosetCount,
      cleaningFlowState,
      entryWalkthroughState: currentTask.entry_walkthrough_state,
      closeoutKeysConfirmed: (currentTask as { closeout_keys_confirmed?: boolean | null }).closeout_keys_confirmed,
      dueMaintenanceCheckIds: dueMaintChecks.map((c) => c.id),
      walkQuickCheckIds: walkQuickChecks.map((c) => c.id),
      guestStagingLineCount: currentTask.checklist_guest_amenity_staging?.length ?? 0,
      guestSetupCompleted: Boolean(currentTask.guest_setup_completed),
    };
  }, [currentTask, turnoverInventoryMeta, cleaningFlowState, dueMaintChecks, walkQuickChecks]);

  const turnoverWizardSteps = useMemo(
    () => (turnoverWizardContext ? buildTurnoverWizardSteps(turnoverWizardContext) : []),
    [turnoverWizardContext]
  );

  const activeWizardStepId = useMemo(
    () => getActiveWizardStepId(turnoverWizardSteps),
    [turnoverWizardSteps]
  );

  const inventorySkippedInSimpleFlow = useMemo(() => {
    if (!currentTask) return true;
    return (
      currentTask.include_inventory_check === false ||
      (turnoverInventoryMeta.postCleanInventoryCount === 0)
    );
  }, [currentTask, turnoverInventoryMeta.postCleanInventoryCount]);

  const rewindSimpleToSegment = useCallback(
    async (segmentIndex: number) => {
      if (!currentTask?.id || !SIMPLE_TURNOVER_V1) return;
      let targetIndex = segmentIndex;
      const segs = simpleFlowSegments(flowChrome);
      const invIndex = segs.indexOf("inventory_single");
      const cleanIndex = segs.indexOf("clean_free");
      const guestIndex = segs.indexOf("guest_setup");
      if (inventorySkippedInSimpleFlow && targetIndex === invIndex) {
        targetIndex = guestIndex >= 0 ? guestIndex : cleanIndex;
      }
      const target = simpleSegmentIdFromIndex(targetIndex, flowChrome);
      if (!target) return;
      const activeIndex = simpleFlowSegmentIndex(activeWizardStepId, flowChrome);
      if (targetIndex > activeIndex) return;

      const { flowPatch, clearChecklistCompleted, clearInventoryCompleted, clearGuestSetupCompleted } =
        buildSimpleFlowRewind(target);
      const prev = readSimpleFlowState(currentTask.entry_walkthrough_state);
      const update: Record<string, unknown> = {
        entry_walkthrough_state: { ...prev, ...flowPatch },
      };
      if (clearChecklistCompleted) update.checklist_completed = false;
      if (clearInventoryCompleted) update.inventory_completed = false;
      if (clearGuestSetupCompleted) update.guest_setup_completed = false;
      if (target === "keys" || target === "walk") {
        update.entry_walkthrough_completed = false;
      }

      const { error } = await supabase.from("cleaning_tasks").update(update).eq("id", currentTask.id);
      if (error) {
        toast({
          title: t("couldntGoBack"),
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
      void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
    },
    [
      activeWizardStepId,
      currentTask,
      effectivePropertyId,
      flowChrome,
      inventorySkippedInSimpleFlow,
      queryClient,
    ],
  );

  // Back leaves the job; progress is already saved, so reopening resumes the same step.
  const goBackSimpleStep = useCallback(() => {
    navigate("/worker/schedule");
  }, [navigate]);

  const needsGuestAmenitySetup = Boolean(
    currentTask &&
      guestStagingLineCount > 0 &&
      !currentTask.guest_setup_completed &&
      cleaningFlowState !== "complete"
  );

  const showGuestAmenitySetupLegacy = Boolean(
    turnoverWizardSteps.length === 0 && needsGuestAmenitySetup && activeTab === "inventory"
  );

  const showTurnoverWizard = Boolean(turnoverWizardSteps.length > 0);

  const leftOutRecall = useMemo(() => {
    if (!currentTask?.checklist_guest_amenity_staging?.length) return null;
    const setup = parseGuestSetupState(currentTask.guest_setup_state);
    return buildLeftOutRecallFromStaging(
      currentTask.checklist_guest_amenity_staging,
      setup.left_out_by_staging_id,
      currentTask.guest_count,
    );
  }, [
    currentTask?.checklist_guest_amenity_staging,
    currentTask?.guest_setup_state,
    currentTask?.guest_count,
  ]);

  const timerStartedAtForProperty =
    currentTask?.property_id === effectivePropertyId &&
    currentTask?.status === "in_progress" &&
    currentTask?.started_at
      ? currentTask.started_at
      : undefined;

  const elapsedLabel = useElapsedSinceLabel(timerStartedAtForProperty);

  const canReportUnitIssue = Boolean(
    currentTask?.id && cleaner?.id && currentTask.status === "in_progress"
  );
  const openUnitIssueReport = () => setUnitIssueReportOpen(true);

  /** After visit timer starts, TurnoverWizard shows title/next/timer — hide duplicate chrome below. */
  const minimalTurnoverChrome = Boolean(showTurnoverWizard && timerStartedAtForProperty);

  // Get property name and address
  // ROOT CAUSE: "Unknown Property" bug - property fetch was failing after cleaner joined via code
  // SOLUTION: Proper loading state + refetch after assignment + fallback to task data
  const taskMatchesUrl = Boolean(taskIdFromUrl && currentTask?.id === taskIdFromUrl);
  const propertyName = taskMatchesUrl
    ? currentTask?.property_name || property?.name || null
    : taskIdFromUrl
      ? null
      : currentTask?.property_name || property?.name || null;
  const propertyAddress = taskMatchesUrl
    ? currentTask?.property_address || property?.address || null
    : taskIdFromUrl
      ? null
      : currentTask?.property_address || property?.address || null;
  const propertyPhotoUrl = property?.property_photo_url || null;

  const inventoryPageSubtitle = useMemo(() => {
    const name = (propertyName ?? "").trim() || (property?.name ?? "").trim() || null;
    const unit = (property?.unit as string | undefined)?.trim();
    const unitLoc = (property?.unit_location as string | undefined)?.trim();
    const detail = [unit, unitLoc].filter(Boolean).join(" · ") || null;
    if (name && detail) return `${name} · ${detail}`;
    if (name) return name;
    return detail;
  }, [propertyName, property?.name, property?.unit, property?.unit_location]);
  
  // If property is still loading and we don't have task data, show loading
  // If property failed to load but assignment exists, show error
  const isPropertyDataLoading = propertyLoading && !currentTask && !property;
  
  // If we have an assignment but no property data, this is a data loading issue
  // This can happen right after joining via code if queries haven't refreshed yet
  const hasAssignmentButNoProperty = assignment && !property && !propertyLoading && !currentTask;

  // Reusable property header component
  const PropertyHeader = ({ hideAccessInstructions = false }: { hideAccessInstructions?: boolean }) => (
    <div className="mb-4 sm:mb-6">
      {/* Property Photo */}
      {propertyPhotoUrl ? (
        <div className="mb-3 sm:mb-4 rounded-xl overflow-hidden">
          <img
            src={propertyPhotoUrl}
            alt={propertyName}
            className="w-full h-28 sm:h-36 object-cover"
          />
        </div>
      ) : (
        <div className="mb-3 sm:mb-4 rounded-xl bg-muted h-20 sm:h-24 flex items-center justify-center">
          <p className="text-muted-foreground text-xs sm:text-sm">{tDynamic("No property photo")}</p>
        </div>
      )}
      <h1 className="text-xl sm:text-2xl font-bold">{tDynamic("Cleaning Portal")}</h1>
      {propertyName ? (
        <>
          <p className="text-sm sm:text-base text-muted-foreground">{propertyName}</p>
          <p className="text-xs sm:text-sm text-muted-foreground">{propertyAddress || tDynamic("No address")}</p>
        </>
      ) : hasAssignmentButNoProperty ? (
        // CRITICAL: Assignment exists but property data failed to load
        // This should never happen - if assignment exists, property should load via join
        // Throw error in dev, show error message in prod
        <div className="space-y-2 border border-destructive/50 bg-destructive/10 rounded-lg p-4">
          <p className="text-sm sm:text-base font-medium text-destructive">
            {process.env.NODE_ENV === 'development' 
              ? "CRITICAL ERROR: Assignment exists but property data is missing"
              : tDynamic("Property data could not be loaded")}
          </p>
          <p className="text-xs text-muted-foreground">
            {tDynamic("Please refresh the page or contact the property owner.")}
          </p>
          {process.env.NODE_ENV === 'development' && (
            <pre className="text-xs mt-2 p-2 bg-muted rounded overflow-auto">
              {JSON.stringify({ propertyId, hasAssignment: !!assignment, hasProperty: !!property, propertyLoading }, null, 2)}
            </pre>
          )}
        </div>
      ) : (
        <p className="text-sm sm:text-base text-muted-foreground">{tDynamic("Loading property details...")}</p>
      )}
      {/* Guest Info */}
      {(currentTask?.guest_count || currentTask?.guest_name) && (
        <div className="mt-2 px-3 py-2 bg-muted rounded-lg">
          <p className="text-xs sm:text-sm text-foreground">
            {currentTask.guest_count && (
              <span className="font-medium">{tDynamic("Guests staying:")} {currentTask.guest_count}</span>
            )}
            {currentTask.guest_count && currentTask.guest_name && <span className="mx-1.5">•</span>}
            {currentTask.guest_name && <span>{currentTask.guest_name}</span>}
          </p>
        </div>
      )}
      {/* Access instructions — hide on job-complete success; cleaner no longer needs entry details */}
      {!hideAccessInstructions && (
      <div className="mt-3">
        <AccessInstructionsView
          instructions={property ? {
            entry_method: property.entry_method ?? null,
            entry_code: property.entry_code ?? null,
            entry_location: property.entry_location ?? null,
            unit_location: property.unit_location ?? null,
            parking_instructions: property.parking_instructions ?? null,
            supply_closet_info: property.supply_closet_info ?? null,
            access_notes: property.access_notes ?? null,
          } : null}
          defaultOpen={true}
        />
      </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <p>{tDynamic("Loading...")}</p>
        </div>
      </AppLayout>
    );
  }

  // Access checks: ACCESS IS GRANTED if all three conditions are met:
  // - profile.role === "cleaner"
  // - cleaner exists
  // - assignment exists
  // ACCESS IS DENIED if any condition fails
  if (!user || profile?.role !== "cleaner") {
    return (
      <AppLayout>
        <Navbar />
        <div className="flex items-center justify-center p-4 sm:p-6" style={{ minHeight: "calc(100vh - 57px)" }}>
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{tDynamic("Access Denied")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                {tDynamic("You must be logged in as a cleaner to access this property.")}
              </p>
              <Button onClick={() => navigate("/login")} className="w-full sm:w-auto">{tDynamic("Go to Login")}</Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (!cleaner) {
    return (
      <AppLayout>
        <Navbar />
        <div className="flex items-center justify-center p-4 sm:p-6" style={{ minHeight: "calc(100vh - 57px)" }}>
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{tDynamic("Cleaner Profile Required")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {tDynamic("Your cleaner profile has not been set up yet. Please contact the property owner.")}
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (!assignment && !currentTask) {
    // No assignment and no task — show Not Assigned, or Task not found when taskId was in URL
    const hadTaskId = !!taskIdFromUrl;
    return (
      <AppLayout>
        <Navbar />
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/worker/schedule", { replace: true })}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {tDynamic("Back to my tasks")}
          </Button>
          <div className="flex items-center justify-center p-4 sm:p-6">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>
                  {hadTaskId ? tDynamic("Task not found") : tDynamic("Not Assigned")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {hadTaskId
                    ? tDynamic("This task was not found or is not assigned to you. Go back to your schedule.")
                    : tDynamic("You are not assigned to this property. Please contact the property owner.")}
                </p>
                <Button
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={() => navigate("/worker/schedule", { replace: true })}
                >
                  {tDynamic("Back to my tasks")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </AppLayout>
    );
  }

  // No active task assigned to this cleaner for this property
  // Show this ONLY if assignment exists (access granted) but no current task
  // Do NOT block access if tasks fail to load - show empty state instead
  if (assignment && !currentTask && !bothCompleted) {
    return (
      <AppLayout>
        <Navbar />
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/worker/schedule")}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {tDynamic("Back to my tasks")}
          </Button>
          <PropertyHeader />

          <Card>
            <CardContent className="py-6 sm:py-8">
              <div className="text-center">
                <CalendarDays className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                <h2 className="text-base sm:text-lg font-semibold mb-2">
                  {taskError ? tDynamic("No tasks yet") : tDynamic("No Task Assigned")}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {taskError 
                    ? tDynamic("Unable to load tasks at this time. Please try again later.")
                    : tDynamic("You don't have any cleaning tasks assigned for this property yet.")
                  }
                </p>
                
                {/* Show next task for THIS property with Start button */}
                {nextTaskForThisProperty && (
                  <div className="mt-4 p-3 sm:p-4 bg-muted rounded-lg text-left">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">{tDynamic("Your next scheduled cleaning:")}</p>
                    <p className="font-medium text-sm sm:text-base">
                      {formatScheduledDate(nextTaskForThisProperty.scheduled_date, "MMMM d, yyyy")}
                    </p>
                    <p className="text-sm font-medium">{nextTaskForThisProperty.property_name}</p>
                    {nextTaskForThisProperty.property_address && (
                      <p className="text-xs sm:text-sm text-muted-foreground">{nextTaskForThisProperty.property_address}</p>
                    )}
                    
                    {/* Start Cleaning / Start Anyway button */}
                    <div className="mt-4">
                      {canStartTurnoverClean(nextTaskForThisProperty.scheduled_date) ? (
                        <Button
                          className="w-full"
                          onClick={() => handleStartTask(nextTaskForThisProperty.id, nextTaskForThisProperty.scheduled_date)}
                          disabled={startCleaningMutation.isPending}
                        >
                          {startCleaningMutation.isPending ? tDynamic("Starting...") : tDynamic("Start Cleaning")}
                        </Button>
                      ) : (
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {formatTurnoverOpensHint(nextTaskForThisProperty.scheduled_date) ||
                            t("cleaningStartNotScheduledDay")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show next task for OTHER properties if no task for this one */}
                {!nextTaskForThisProperty && nextTask && (
                  <div className="mt-4 p-3 sm:p-4 bg-muted rounded-lg text-left">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">{tDynamic("Your next scheduled cleaning:")}</p>
                    <p className="font-medium text-sm sm:text-base">
                      {formatScheduledDate(nextTask.scheduled_date, "MMMM d, yyyy")}
                    </p>
                    <p className="text-sm font-medium">{nextTask.property_name}</p>
                    {nextTask.property_address && (
                      <p className="text-xs sm:text-sm text-muted-foreground">{nextTask.property_address}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Case 4: Both completed - show completion screen
  if (bothCompleted) {
    return (
      <AppLayout>
        <div data-cleaner-turnover-chrome="1" className="min-w-0 bg-[color:var(--pv-bg)]">
          <Navbar className={TURNOVER_RUNNER_NAVBAR_CLASSNAME} />
          <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 min-w-0">
            <PropertyHeader hideAccessInstructions />

            <Card className="rounded-[var(--pv-r-md)] border-[color:color-mix(in_srgb,var(--pv-teal)_28%,var(--pv-line))] bg-[color:var(--pv-teal-soft)] shadow-[var(--pv-shadow-card)]">
              <CardContent className="py-6 sm:py-8 text-center">
                <PartyPopper className="mx-auto mb-3 h-12 w-12 text-[color:var(--pv-teal)] sm:mb-4 sm:h-16 sm:w-16" />
                <h2 className="mb-2 text-xl font-bold text-[color:var(--pv-ink)] sm:text-2xl">
                  {tDynamic("Cleaning Completed!")}
                </h2>
                <p className="text-sm text-[color:var(--pv-ink-2)] sm:text-base">
                  {tDynamic("Thank you. No tasks remaining for this property.")}
                </p>
                {currentTask?.duration_minutes != null && (
                  <p className="mt-2 text-sm font-medium text-[color:var(--pv-teal)] sm:text-base">
                    {tDynamic("Total cleaning time:")} {currentTask.duration_minutes} {tDynamic("minutes")}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="mt-4 rounded-[var(--pv-r-md)] border-[color:var(--pv-line)] bg-[color:var(--pv-surface)] shadow-[var(--pv-shadow-card)] sm:mt-6">
              <CardContent className="py-4 sm:py-6">
                {nextTask && nextTask.id !== currentTask?.id ? (
                  <div className="text-center">
                    <CalendarDays className="mx-auto mb-2 h-8 w-8 text-[color:var(--pv-teal)] sm:mb-3 sm:h-10 sm:w-10" />
                    <p className="mb-2 text-sm sm:text-lg">
                      {tDynamic("Your next cleaning is on")}{" "}
                      <span className="font-semibold text-[color:var(--pv-ink)]">
                        {formatScheduledDate(nextTask.scheduled_date, "MMMM d, yyyy")}
                      </span>{" "}
                      {tDynamic("for")}{" "}
                      <span className="font-semibold text-[color:var(--pv-ink)]">{nextTask.property_name}</span>
                    </p>
                    {nextTask.property_address && (
                      <p className="mb-4 text-xs text-[color:var(--pv-ink-2)] sm:text-sm">{nextTask.property_address}</p>
                    )}
                    <p className="mb-4 text-xs text-[color:var(--pv-ink-3)] sm:text-sm">{t("afterCleaningReturnToSchedule")}</p>
                    <Button
                      onClick={() => navigate("/worker/schedule?tab=upcoming", { replace: true })}
                      className="w-full sm:w-auto"
                    >
                      {t("viewMyTaskList")}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <PartyPopper className="mx-auto mb-2 h-8 w-8 text-[color:var(--pv-ink-3)] sm:mb-3 sm:h-10 sm:w-10" />
                    <p className="mb-2 text-sm font-medium text-[color:var(--pv-ink)] sm:text-base">
                      {tDynamic("You're done for today!")}
                    </p>
                    <p className="mb-4 text-xs text-[color:var(--pv-ink-2)] sm:text-sm">
                      {tDynamic("You currently have no upcoming assigned tasks.")}
                    </p>
                    <Button onClick={() => navigate("/worker/schedule?tab=upcoming", { replace: true })} className="w-full sm:w-auto">
                      {t("viewMyTaskList")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Scheduled task + taskId: intro only — Start task clocks in immediately (timer runs during pre-visit steps).
  if (showScheduledTaskIntro && currentTask) {
    const accessInstructions = property
      ? {
          entry_method: property.entry_method ?? null,
          entry_code: property.entry_code ?? null,
          entry_location: property.entry_location ?? null,
          unit_location: property.unit_location ?? null,
          parking_instructions: property.parking_instructions ?? null,
          supply_closet_info: property.supply_closet_info ?? null,
          access_notes: property.access_notes ?? null,
        }
      : null;

    return (
      <AppLayout>
        {!SIMPLE_TURNOVER_V1 ? <Navbar /> : null}
        <div
          className={
            SIMPLE_TURNOVER_V1
              ? "min-h-[100dvh] w-full min-w-0 bg-white"
              : "w-full max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 min-w-0"
          }
        >
          <TaskPreStartView
            task={currentTask}
            propertyName={currentTask.property_name || property?.name || null}
            propertyAddress={currentTask.property_address || property?.formatted_address || [property?.address, property?.city].filter(Boolean).join(", ") || null}
            propertyPhotoUrl={property?.property_photo_url ?? null}
            accessInstructions={accessInstructions}
            startDisabled={!canStartTurnoverClean(currentTask.scheduled_date)}
            startBlockedHint={
              formatTurnoverOpensHint(currentTask.scheduled_date) || t("cleaningStartNotScheduledDay")
            }
            isStarting={startCleaningMutation.isPending}
            onStart={async () => {
              if (!canStartTurnoverClean(currentTask.scheduled_date)) {
                toast({
                  title: t("cleaningStartNotScheduledDayTitle"),
                  description: t("cleaningStartNotScheduledDay"),
                  variant: "default",
                });
                return;
              }
              try {
                await startCleaningMutation.mutateAsync({
                  taskId: currentTask.id,
                  propertyId: effectivePropertyId!,
                  enforceScheduledDay: true,
                });
              } catch (error) {
                console.error("[CleanerPortal] Failed to start task from intro:", error);
                const msg = error instanceof Error ? error.message : "";
                if (msg === "CLEANING_START_NOT_SCHEDULED_DAY") {
                  toast({
                    title: t("cleaningStartNotScheduledDayTitle"),
                    description: t("cleaningStartNotScheduledDay"),
                    variant: "default",
                  });
                } else if (msg === "CLEANING_START_NOT_ASSIGNED_CLEANER") {
                  toast({
                    title: t("cleaningStartNotAssignedTitle"),
                    description: t("cleaningStartNotAssigned"),
                    variant: "default",
                  });
                } else {
                  toast({
                    title: t("error"),
                    description: t("failedToStartTimer") || "Could not start task",
                    variant: "destructive",
                  });
                }
              }
            }}
            onBack={() => navigate("/worker/schedule")}
            startButtonLabel={SIMPLE_TURNOVER_V1 ? t("start") : tDynamic("Start task")}
            windowLabel={currentTask.window_label ?? null}
            windowTimes={
              (currentTask as { window_times_detail_primary?: string | null }).window_times_detail_primary ??
              currentTask.window_times_label ??
              null
            }
            windowTimesSecondary={
              (currentTask as { window_times_detail_secondary?: string | null }).window_times_detail_secondary ??
              (currentTask as { window_times_secondary?: string | null }).window_times_secondary ??
              null
            }
            windowTimesTone={
              ((currentTask as { window_times_tone?: string | null }).window_times_tone as
                | "default"
                | "teal"
                | "amber"
                | "muted"
                | null) ?? null
            }
            windowChip={currentTask.window_chip ?? null}
            guestLeft={Boolean(currentTask.guest_left)}
            waitingOnGuest={Boolean((currentTask as { waiting_on_guest?: boolean }).waiting_on_guest)}
            sameDay={Boolean(currentTask.same_day_turnover)}
            tightWindow={Boolean(currentTask.tight_window)}
            t={t}
            pendingReorders={pendingPickupReorders}
            cleanerId={cleaner?.id ?? null}
            propertyId={effectivePropertyId ?? null}
          />
        </div>
      </AppLayout>
    );
  }

  // In progress but entry walkthrough not finished: access → location → proof (visit timer already started on intro).
  if (showPreVisitWizard && currentTask) {
    const preVisitPillLabel = (() => {
      const street =
        (property?.address || "").trim() ||
        (currentTask.property_address || "").split(",")[0]?.trim() ||
        "";
      const unit = (property?.unit_location || "").trim();
      if (street && unit) return `${street} · ${unit}`;
      if (street) return street;
      if (unit) return unit;
      return (currentTask.property_name || "").trim() || null;
    })();

    return (
      <AppLayout>
        <Navbar />
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 min-w-0">
          {currentTask.started_at && preVisitElapsedLabel ? (
            <div className="sticky top-0 z-30 -mx-4 mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--pv-line,hsl(var(--border)))] bg-background/95 px-4 py-2 backdrop-blur-md sm:mx-0 sm:rounded-lg sm:border">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                <span>{tDynamic("Visit time")}</span>
                <span className="font-mono tabular-nums tracking-tight">{preVisitElapsedLabel}</span>
              </div>
              {cleaner?.id ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1 border-amber-300/80 bg-amber-50/80 px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                  onClick={openUnitIssueReport}
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {tDynamic("Report")}
                </Button>
              ) : null}
            </div>
          ) : null}
          <>
              {preVisitStep !== 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    if (preVisitStep > 0) {
                      if (preVisitStep === 2) setPreVisitLocation(null);
                      setPreVisitStep((s) => s - 1);
                    } else {
                      navigate("/worker/schedule");
                    }
                  }}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {tDynamic("Back")}
                </Button>
              )}
              {preVisitStep === 0 && !cleaner?.id && (
                <p className="text-sm text-muted-foreground mb-4">{tDynamic("Loading...")}</p>
              )}
              {preVisitStep === 0 && cleaner?.id && (
                <div
                  data-previsit-ui="1"
                  className="-mx-4 min-h-[calc(100dvh-10rem)] bg-[var(--pv-bg)] px-4 pb-6 pt-1 sm:mx-0 sm:rounded-2xl"
                >
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-2 shrink-0 text-[var(--pv-ink-2)] hover:bg-[var(--pv-surface-2)] hover:text-[var(--pv-ink)]"
                      onClick={() => navigate("/worker/schedule")}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {tDynamic("Back")}
                    </Button>
                    <div className="ml-auto flex min-w-0 items-center gap-2">
                      {!preVisitElapsedLabel ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 shrink-0 gap-1 border-amber-300/80 bg-amber-50/80 px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                          onClick={openUnitIssueReport}
                        >
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {tDynamic("Report")}
                        </Button>
                      ) : null}
                    {preVisitPillLabel ? (
                      <div
                        className="inline-flex max-w-full sm:max-w-[58%] min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium"
                        style={{
                          borderColor: "var(--pv-line)",
                          background: "var(--pv-surface)",
                          color: "var(--pv-ink-2)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: "var(--pv-teal)" }}
                          aria-hidden
                        />
                        <span className="truncate">{preVisitPillLabel}</span>
                      </div>
                    ) : null}
                    </div>
                  </div>
                  <PreVisitStepHeader stepIndex={0} variant="turnoverV1" />
                  <EntryWalkthroughStep
                    sectionMode="access"
                    omitEmbeddedCardHeader
                    turnoverPrevisitMobileUi
                    propertyId={effectivePropertyId!}
                    taskId={currentTask.id}
                    checklistId={currentTask.checklist_id}
                    cleanerId={cleaner.id}
                    tDynamic={tDynamic}
                    accessInstructions={null}
                    existingEntryWalkthroughState={
                      (currentTask.entry_walkthrough_state as Record<string, unknown> | null) ?? null
                    }
                    onAccessPhaseComplete={async () => {
                      await queryClient.refetchQueries({
                        queryKey: ["current-cleaning-task", effectivePropertyId, cleaner.id, taskIdFromUrl ?? ""],
                      });
                      setPreVisitStep(1);
                    }}
                    onCompleted={() => {}}
                  />
                </div>
              )}
              {preVisitStep === 1 && (
                <>
                  <PreVisitStepHeader stepIndex={1} variant="turnoverV1" />
                  {propertyLoading ? (
                    <p className="text-sm text-muted-foreground py-6">{tDynamic("Loading property details...")}</p>
                  ) : property?.latitude == null ||
                    property?.longitude == null ||
                    !Number.isFinite(Number(property.latitude)) ||
                    !Number.isFinite(Number(property.longitude)) ? (
                    <Alert variant="destructive" className="mb-4">
                      <AlertDescription>{t("preVisitPropertyCoordsMissing")}</AlertDescription>
                    </Alert>
                  ) : (
                    <div
                      data-previsit-ui="1"
                      className="-mx-4 rounded-2xl bg-[var(--pv-bg)] px-4 pb-4 pt-1 sm:mx-0"
                    >
                      <CleaningTimer
                      visitFlowPhase="location_only"
                      embedInPreVisitFlow
                      onLocationConfirmed={async (snap) => {
                        try {
                          setPreVisitLocation(snap);
                          await queryClient.refetchQueries({
                            queryKey: ["current-cleaning-task", effectivePropertyId, cleaner?.id ?? "", taskIdFromUrl ?? ""],
                          });
                          setPreVisitStep(2);
                        } catch (e) {
                          console.error(e);
                          toast({
                            title: tDynamic("Error"),
                            description: tDynamic("Could not continue. Please try again."),
                            variant: "destructive",
                          });
                        }
                      }}
                      taskId={currentTask.id}
                      propertyId={effectivePropertyId!}
                      startedAt={undefined}
                      completedAt={undefined}
                      durationMinutes={undefined}
                      scheduledDate={currentTask.scheduled_date}
                      unitEmptyOnArrival={undefined}
                      propertyLat={property.latitude}
                      propertyLng={property.longitude}
                      propertyAddress={
                        property?.formatted_address ||
                        [property?.address, property?.city].filter(Boolean).join(", ") ||
                        undefined
                      }
                      confirmLocationStepEnabled={false}
                    />
                    </div>
                  )}
                </>
              )}
              {preVisitStep === 2 && preVisitLocation && cleaner?.id && (
                <div
                  data-previsit-ui="1"
                  className="-mx-4 rounded-2xl bg-[var(--pv-bg)] px-4 pb-4 pt-1 sm:mx-0"
                >
                  <PreVisitStepHeader stepIndex={2} variant="turnoverV1" />
                  <EntryWalkthroughStep
                    sectionMode="proof"
                    omitEmbeddedCardHeader
                    turnoverPrevisitMobileUi
                    propertyId={effectivePropertyId!}
                    taskId={currentTask.id}
                    checklistId={currentTask.checklist_id}
                    cleanerId={cleaner.id}
                    tDynamic={tDynamic}
                    accessInstructions={null}
                    existingEntryWalkthroughState={
                      (currentTask.entry_walkthrough_state as Record<string, unknown> | null) ?? null
                    }
                    pendingStartLocation={preVisitLocation}
                    preVisitTimerAddressLabel={
                      property?.formatted_address ||
                      [property?.address, property?.city].filter(Boolean).join(", ") ||
                      null
                    }
                    onStartVisitTimer={async ({ extraDirty, extraDirtyNotes }) => {
                      await startCleaningMutation.mutateAsync({
                        taskId: currentTask.id,
                        propertyId: effectivePropertyId!,
                        extraDirty,
                        extraDirtyNotes,
                        startLocation: preVisitLocation,
                        earlyCleaningStarted: false,
                        enforceScheduledDay: true,
                      });
                    }}
                    onCompleted={() => {
                      void queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                      void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                      void queryClient.invalidateQueries({ queryKey: ["in-progress-cleaning-tasks"] });
                      void queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] });
                    }}
                  />
                </div>
              )}
          </>
          {cleaner?.id ? (
            <TurnoverUnitIssueReportDialog
              open={unitIssueReportOpen}
              onOpenChange={setUnitIssueReportOpen}
              propertyId={effectivePropertyId!}
              cleaningTaskId={currentTask.id}
              cleanerId={cleaner.id}
              checklistId={currentTask.checklist_id}
              checklistName={currentTask.checklist_name}
              tDynamic={tDynamic}
            />
          ) : null}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-cleaner-turnover-chrome="1" className="min-w-0">
      {/* Slice 14 job steps: full-bleed Claude screens — hide site Navbar chrome */}
      {!(SIMPLE_TURNOVER_V1 && showTurnoverWizard && activeWizardStepId !== "start_visit") ? (
        <Navbar className={TURNOVER_RUNNER_NAVBAR_CLASSNAME} />
      ) : null}

      {/* Floating header - visible across all tabs when scrolled */}
      {currentTask && !bothCompleted && !minimalTurnoverChrome && !SIMPLE_TURNOVER_V1 && (
        <FloatingChecklistHeader
          checklistName={displayChecklistName || currentTask.checklist_name || t("checklist")}
          completedCount={progressData.completedCount}
          totalTasks={progressData.totalTasks}
          timerStartedAt={
              currentTask.property_id === effectivePropertyId &&
              currentTask.status === "in_progress" &&
              currentTask.started_at
                ? currentTask.started_at
                : undefined
            }
          isVisible={floatingHeaderVisible}
          isSaving={progressData.isSaving}
          flowSummary={flowSummaryShort}
          onReportIssue={canReportUnitIssue ? openUnitIssueReport : undefined}
        />
      )}
      
      <div
        className={
          SIMPLE_TURNOVER_V1 && showTurnoverWizard
            ? "w-full min-w-0 overflow-x-hidden bg-white"
            : "w-full max-w-2xl mx-auto px-4 md:px-6 py-4 md:py-6 min-w-0 overflow-x-hidden space-y-4 md:space-y-6"
        }
      >
        {/* Slice 14: step screens own chrome — no legacy back row above duplicate progress */}
        {!(SIMPLE_TURNOVER_V1 && showTurnoverWizard && activeWizardStepId !== "start_visit" && activeWizardStepId !== "done") ? (
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 text-muted-foreground hover:text-foreground w-full justify-start md:w-auto"
            onClick={() => navigate("/worker/schedule")}
          >
            <ChevronLeft className="h-4 w-4 mr-1 shrink-0" />
            <span className="truncate">{tDynamic("Back to my tasks")}</span>
          </Button>
        ) : null}
        {/* Main header ref: property chrome when shown; empty anchor when wizard shows single timer strip */}
        <div ref={mainHeaderRef}>
          {!minimalTurnoverChrome ? (
            <>
              <PropertyHeader />
              {currentTask && (
                <p className="text-xs sm:text-sm font-medium text-primary -mt-2 sm:-mt-4 mb-4 sm:mb-6">
                  {tDynamic("Scheduled:")}{" "}
                  {formatScheduledDate(currentTask.scheduled_date, "MMMM d, yyyy")}
                </p>
              )}

              {currentTask && (currentTask.reservation_check_in_time || currentTask.reservation_check_out_time) && (
                <div className="mb-4 rounded-lg border bg-muted/30 px-3 py-2 text-sm break-words">
                  <span className="text-muted-foreground">{tDynamic("Reservation:")}{" "}</span>
                  {currentTask.reservation_check_in_time && (
                    <span>
                      {tDynamic("Check-in")} {currentTask.reservation_check_in_time}
                    </span>
                  )}
                  {currentTask.reservation_check_in_time && currentTask.reservation_check_out_time && " · "}
                  {currentTask.reservation_check_out_time && (
                    <span>
                      {tDynamic("Check-out")} {currentTask.reservation_check_out_time}
                    </span>
                  )}
                </div>
              )}

              {currentTask && (() => {
                const effectiveTimes = computeEffectiveTimes(
                  {
                    override_check_in_time: currentTask.override_check_in_time,
                    override_check_out_time: currentTask.override_check_out_time,
                  },
                  {
                    check_in_time: currentTask.reservation_check_in_time,
                    check_out_time: currentTask.reservation_check_out_time,
                  }
                );
                return (
                  <CheckoutTimeBanner
                    checkoutTime={effectiveTimes.checkOut}
                    earlyCheckoutDetected={Boolean(currentTask.guest_left)}
                    unitEmptyOnArrival={currentTask.unit_empty_on_arrival}
                    sticky
                  />
                );
              })()}
            </>
          ) : null}
        </div>
        {currentTask && showTurnoverWizard ? (
          <TurnoverWizard
            steps={turnoverWizardSteps}
            activeStepId={activeWizardStepId}
            tDynamic={tDynamic}
            variant={SIMPLE_TURNOVER_V1 ? "bare" : "legacy"}
            timerSummary={
              !SIMPLE_TURNOVER_V1 && elapsedLabel ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {elapsedLabel}
                </span>
              ) : undefined
            }
            onReportIssue={!SIMPLE_TURNOVER_V1 && canReportUnitIssue ? openUnitIssueReport : undefined}
          >
            {activeWizardStepId === "start_visit" && (
              <CleaningTimer
                taskId={currentTask.id}
                propertyId={effectivePropertyId!}
                startedAt={timerStartedAtForProperty ?? null}
                completedAt={undefined}
                durationMinutes={undefined}
                scheduledDate={currentTask.scheduled_date}
                unitEmptyOnArrival={undefined}
                propertyLat={property?.latitude ?? undefined}
                propertyLng={property?.longitude ?? undefined}
                propertyAddress={
                  property?.formatted_address ||
                  [property?.address, property?.city, property?.state_province, property?.postal_code]
                    .filter(Boolean)
                    .join(", ") ||
                  undefined
                }
                confirmLocationStepEnabled
              />
            )}
            {activeWizardStepId !== "start_visit" && timerStartedAtForProperty && !SIMPLE_TURNOVER_V1 && (
              <CleaningTimer
                taskId={currentTask.id}
                propertyId={effectivePropertyId!}
                startedAt={timerStartedAtForProperty}
                completedAt={currentTask.property_id === effectivePropertyId ? (currentTask.completed_at ?? undefined) : undefined}
                durationMinutes={currentTask.property_id === effectivePropertyId ? (currentTask.duration_minutes ?? undefined) : undefined}
                scheduledDate={currentTask.scheduled_date}
                unitEmptyOnArrival={
                  currentTask.property_id === effectivePropertyId ? (currentTask.unit_empty_on_arrival ?? undefined) : undefined
                }
                propertyLat={property?.latitude ?? undefined}
                propertyLng={property?.longitude ?? undefined}
                propertyAddress={
                  property?.formatted_address ||
                  [property?.address, property?.city, property?.state_province, property?.postal_code]
                    .filter(Boolean)
                    .join(", ") ||
                  undefined
                }
                confirmLocationStepEnabled={false}
                hideRunningElapsedCard
              />
            )}
            {activeWizardStepId === "arrival_check" && (
              <EntryWalkthroughStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                checklistId={currentTask.checklist_id}
                cleanerId={cleaner?.id ?? ""}
                tDynamic={tDynamic}
                onCompleted={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "walk" && (
              <RoomAwareStep
                phase="before"
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                cleanerId={cleaner?.id || user?.id || ""}
                checklistName={currentTask.checklist_name}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "walk_checks" && (
              <SimpleQuickCheckStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                cleanerId={cleaner?.id || user?.id || ""}
                checklistName={currentTask.checklist_name}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                quickChecks={walkQuickChecks.map((q) => ({ id: q.id, label: q.label }))}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "maintenance" && (
              <SimpleMaintenanceDueStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                dueChecks={dueMaintChecks}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "clean_free" && (
              <SimpleCleanStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                checklistIds={currentTask.assigned_checklists?.length ? currentTask.assigned_checklists.map((c) => c.checklist_id) : currentTask.checklist_id ? [currentTask.checklist_id] : []}
                startedAt={currentTask.started_at}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onReportIssue={canReportUnitIssue ? openUnitIssueReport : undefined}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onUndoMaintenanceSkip={() => {
                  const maintIndex = simpleFlowSegmentIndex("maintenance", flowChrome);
                  if (maintIndex >= 0) void rewindSimpleToSegment(maintIndex);
                }}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "after_photos" && (
              <RoomAwareStep
                phase="after"
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "drop_keys" && (
              <SimpleDropKeysStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                dropSpotHint={(() => {
                  const bits = [
                    property?.entry_location,
                    property?.entry_method === "lockbox" ? "Lockbox" : property?.entry_method,
                    property?.unit_location,
                  ].filter((x) => typeof x === "string" && x.trim());
                  return bits.length ? bits.slice(0, 2).join(" · ") : null;
                })()}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={async () => {
                  try {
                    await markTaskCompleteMutation.mutateAsync({
                      taskId: currentTask.id,
                      propertyId: effectivePropertyId!,
                    });
                  } catch {
                    /* still advance */
                  }
                  setCleaningFlowState("complete");
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "keys" && (
              <SimpleKeysStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                entryWalkthroughState={currentTask.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                initialPhotoUrl={readSimpleFlowState(currentTask.entry_walkthrough_state).keys_photo_url}
                access={{
                  entry_location: property?.entry_location ?? null,
                  entry_method: property?.entry_method ?? null,
                  entry_code: property?.entry_code ?? null,
                  key_location_reference_urls:
                    (property as { key_location_reference_urls?: string[] | null } | null)
                      ?.key_location_reference_urls ?? [],
                }}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "guest_setup" && currentTask && (
              <GuestAmenitySetupStep
                propertyId={effectivePropertyId!}
                taskId={currentTask.id}
                initialGuestSetupState={currentTask.guest_setup_state ?? null}
                stagingLines={currentTask.checklist_guest_amenity_staging ?? null}
                hostGuestAmenitiesInstructions={currentTask.checklist_guest_amenities_instructions ?? null}
                guestCount={currentTask.guest_count ?? null}
                simpleChrome
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onCompleted={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "inventory_single" && (
              <SimpleInventoryStep
                propertyId={effectivePropertyId!}
                cleaningTaskId={currentTask.id}
                cleanerId={cleaner?.id ?? null}
                readOnly={cleaningFlowState === "complete"}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                leftOutRecall={leftOutRecall}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                  queryClient.invalidateQueries({ queryKey: ["inventory-cleaner", effectivePropertyId!] });
                }}
              />
            )}
            {((!SIMPLE_TURNOVER_V1 && activeWizardStepId === "cleaning") ||
              (!SIMPLE_TURNOVER_V1 && activeWizardStepId === "inventory_single")) && (
              <>
                {showChecklistSuccess && (
                  <Alert className="mb-4 sm:mb-6 border-green-500 bg-green-50 shadow-md">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <AlertDescription className="text-green-800 font-medium text-sm sm:text-base">
                      {tDynamic("Cleaning checklist submitted successfully!")}
                      {inventoryItems && inventoryItems.length > 0 && (
                        <span className="block mt-1 text-sm">
                          {tDynamic("Please complete the inventory check next.")}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                {timerStartedAtForProperty && activeWizardStepId !== "start_visit" && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-4 md:mb-6">
                    <Button
                      variant="outline"
                      className="w-full sm:flex-1 min-w-0"
                      onClick={() => setMessagesOpen(true)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">{tDynamic("Messages")}</span>
                    </Button>
                    {activeWizardStepId === "cleaning" &&
                      cleaningFlowState === "checklist" &&
                      progressData.completedCount > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-full sm:w-auto shrink-0"
                          onClick={() => setShowRestartConfirm(true)}
                          title={tDynamic("Restart cleaning")}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                  </div>
                )}
                {cleaningFlowState === "inventory" && (
                  <Alert className="mb-4 sm:mb-6 border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800 text-sm">
                      {tDynamic("Cleaning checklist completed. Please finish inventory.")}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="w-full space-y-4">
                  <div
                    className={cn(
                      activeWizardStepId !== "cleaning" && "hidden"
                    )}
                  >
                    <CleaningTab
                      propertyId={effectivePropertyId!}
                      taskId={currentTask?.id}
                      readOnly={cleaningFlowState === "complete"}
                      checklistId={currentTask?.assigned_checklists?.[0]?.checklist_id ?? currentTask?.checklist_id}
                      checklistName={currentTask?.assigned_checklists?.[0]?.checklist_name ?? currentTask?.checklist_name}
                      timerStartedAt={timerStartedAtForProperty}
                      onProgressChange={setProgressData}
                      onChecklistNameChange={setDisplayChecklistName}
                      onChecklistSubmitted={handleChecklistSubmitted}
                      entryWalkthroughState={currentTask?.entry_walkthrough_state ?? null}
                      entryWalkthroughCompleted={Boolean(currentTask?.entry_walkthrough_completed)}
                      hideInlineRoomInventory={Boolean(
                        currentTask?.id &&
                          currentTask?.include_inventory_check !== false &&
                          turnoverInventoryMeta.postCleanCount > 0
                      )}
                      hideInlineElapsedLabel={minimalTurnoverChrome}
                    />
                  </div>
                  <div
                    className={cn(
                      activeWizardStepId !== "inventory_single" && "hidden"
                    )}
                  >
                    <InventoryCheckTab
                      propertyId={effectivePropertyId!}
                      cleanerId={cleaner?.id || ""}
                      reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                      readOnly={cleaningFlowState === "complete"}
                      onInventorySubmitted={handleInventorySubmitted}
                      onCompleteCleaning={
                        currentTask?.id
                          ? async () => {
                              await markTaskCompleteMutation.mutateAsync({
                                taskId: currentTask.id,
                                propertyId: effectivePropertyId!,
                              });
                            }
                          : undefined
                      }
                      allTasksCompleted={
                        progressData.totalTasks > 0 &&
                        progressData.completedCount === progressData.totalTasks
                      }
                      cleaningTaskId={currentTask?.id ?? null}
                      scheduledDate={currentTask?.scheduled_date ?? null}
                      inventoryGuestPhaseCompleted={currentTask?.inventory_guest_phase_completed}
                      guestSetupCompleted={Boolean(currentTask?.guest_setup_completed)}
                      guestStagingLineCount={currentTask?.checklist_guest_amenity_staging?.length ?? 0}
                      wizardInventoryStepId={activeWizardStepId}
                      propertySubtitle={inventoryPageSubtitle}
                    />
                  </div>
                </div>
              </>
            )}
            {activeWizardStepId === "done" && SIMPLE_TURNOVER_V1 && (
              <SimpleDoneStep
                photoCount={(() => {
                  const s = readSimpleFlowState(currentTask.entry_walkthrough_state);
                  return (
                    collectSimplePhotoUrls(s, "before").length +
                    collectSimplePhotoUrls(s, "after").length +
                    (s.keys_photo_url ? 1 : 0)
                  );
                })()}
                issueCount={
                  readSimpleFlowState(currentTask.entry_walkthrough_state).inspect_findings?.filter(
                    (f) => f.status === "missing" || f.status === "broken",
                  ).length ?? 0
                }
                startedAt={currentTask.started_at}
                taskId={currentTask.id}
                finishedAt={currentTask.completed_at}
                onJobs={() => navigate("/worker/schedule?tab=past")}
              />
            )}
            {activeWizardStepId === "done" && !SIMPLE_TURNOVER_V1 && (
              <Alert className="border-[color:color-mix(in_srgb,var(--pv-teal)_35%,var(--pv-line))] bg-[color:var(--pv-teal-soft)] text-[color:var(--pv-ink)] [&>svg]:text-[color:var(--pv-teal)]">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription className="text-[color:var(--pv-ink-2)]">
                  {tDynamic("All steps for this turnover are complete.")}
                </AlertDescription>
              </Alert>
            )}
          </TurnoverWizard>
        ) : (
          <>
            {currentTask && (
              <CleaningTimer
                taskId={currentTask.id}
                propertyId={effectivePropertyId!}
                startedAt={
                  currentTask.property_id === effectivePropertyId &&
                  currentTask.status === "in_progress" &&
                  currentTask.started_at
                    ? (currentTask.started_at ?? undefined)
                    : undefined
                }
                completedAt={
                  currentTask.property_id === effectivePropertyId ? (currentTask.completed_at ?? undefined) : undefined
                }
                durationMinutes={
                  currentTask.property_id === effectivePropertyId ? (currentTask.duration_minutes ?? undefined) : undefined
                }
                scheduledDate={currentTask.scheduled_date}
                unitEmptyOnArrival={
                  currentTask.property_id === effectivePropertyId ? (currentTask.unit_empty_on_arrival ?? undefined) : undefined
                }
                propertyLat={property?.latitude ?? undefined}
                propertyLng={property?.longitude ?? undefined}
                propertyAddress={
                  property?.formatted_address ||
                  [property?.address, property?.city, property?.state_province, property?.postal_code]
                    .filter(Boolean)
                    .join(", ") ||
                  undefined
                }
                confirmLocationStepEnabled={false}
              />
            )}

            {showEntryWalkthrough && currentTask ? (
              <>
                <TurnoverStepStrip
                  phase={turnoverPhase}
                  showEntryStep={showEntryColumn}
                  splitInventory={false}
                  tDynamic={tDynamic}
                  className="mb-4"
                />
                <EntryWalkthroughStep
                  propertyId={effectivePropertyId!}
                  taskId={currentTask.id}
                  checklistId={currentTask.checklist_id}
                  cleanerId={cleaner?.id ?? ""}
                  tDynamic={tDynamic}
                  onCompleted={() => {
                    queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                    queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                  }}
                />
              </>
            ) : (
              <>
                {showChecklistSuccess && (
                  <Alert className="mb-4 sm:mb-6 border-green-500 bg-green-50 shadow-md">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <AlertDescription className="text-green-800 font-medium text-sm sm:text-base">
                      {tDynamic("Cleaning checklist submitted successfully!")}
                      {inventoryItems && inventoryItems.length > 0 && (
                        <span className="block mt-1 text-sm">
                          {tDynamic("Please complete the inventory check next.")}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {currentTask && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-4 md:mb-6">
                    <Button
                      variant="outline"
                      className="w-full sm:flex-1 min-w-0"
                      onClick={() => setMessagesOpen(true)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">{tDynamic("Messages")}</span>
                    </Button>
                    {cleaningFlowState === "checklist" && progressData.completedCount > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-full sm:w-auto shrink-0"
                        onClick={() => setShowRestartConfirm(true)}
                        title={tDynamic("Restart cleaning")}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}

                {cleaningFlowState === "inventory" && (
                  <Alert className="mb-4 sm:mb-6 border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800 text-sm">
                      {tDynamic("Cleaning checklist completed. Please finish inventory.")}
                    </AlertDescription>
                  </Alert>
                )}

                <TurnoverStepStrip
                  phase={turnoverPhase}
                  showEntryStep={showEntryColumn}
                  splitInventory={false}
                  tDynamic={tDynamic}
                  className="mb-4"
                />

                <div className="w-full space-y-4">
                  <div className={cn(activeTab !== "cleaning" && "hidden")}>
                    <CleaningTab
                      propertyId={effectivePropertyId!}
                      taskId={currentTask?.id}
                      readOnly={cleaningFlowState === "complete"}
                      checklistId={currentTask?.assigned_checklists?.[0]?.checklist_id ?? currentTask?.checklist_id}
                      checklistName={currentTask?.assigned_checklists?.[0]?.checklist_name ?? currentTask?.checklist_name}
                      timerStartedAt={
                        currentTask?.property_id === effectivePropertyId &&
                        currentTask?.status === "in_progress" &&
                        currentTask?.started_at
                          ? currentTask?.started_at
                          : undefined
                      }
                      onProgressChange={setProgressData}
                      onChecklistNameChange={setDisplayChecklistName}
                      onChecklistSubmitted={handleChecklistSubmitted}
                      entryWalkthroughState={currentTask?.entry_walkthrough_state ?? null}
                      entryWalkthroughCompleted={Boolean(currentTask?.entry_walkthrough_completed)}
                      hideInlineRoomInventory={Boolean(
                        currentTask?.id &&
                          currentTask?.include_inventory_check !== false &&
                          turnoverInventoryMeta.postCleanCount > 0
                      )}
                    />
                  </div>
                  <div className={cn(activeTab !== "inventory" && "hidden")}>
                    {showGuestAmenitySetupLegacy && currentTask ? (
                      <GuestAmenitySetupStep
                        propertyId={effectivePropertyId!}
                        taskId={currentTask.id}
                        initialGuestSetupState={currentTask.guest_setup_state ?? null}
                        stagingLines={currentTask.checklist_guest_amenity_staging ?? null}
                        hostGuestAmenitiesInstructions={currentTask.checklist_guest_amenities_instructions ?? null}
                        onCompleted={() => {
                          queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", effectivePropertyId!] });
                          queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", effectivePropertyId!] });
                        }}
                      />
                    ) : (
                      <InventoryCheckTab
                        propertyId={effectivePropertyId!}
                        cleanerId={cleaner?.id || ""}
                        reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                        readOnly={cleaningFlowState === "complete"}
                        onInventorySubmitted={handleInventorySubmitted}
                        onCompleteCleaning={
                          currentTask?.id
                            ? async () => {
                                await markTaskCompleteMutation.mutateAsync({
                                  taskId: currentTask.id,
                                  propertyId: effectivePropertyId!,
                                });
                              }
                            : undefined
                        }
                        allTasksCompleted={
                          progressData.totalTasks > 0 &&
                          progressData.completedCount === progressData.totalTasks
                        }
                        cleaningTaskId={currentTask?.id ?? null}
                        scheduledDate={currentTask?.scheduled_date ?? null}
                        inventoryGuestPhaseCompleted={currentTask?.inventory_guest_phase_completed}
                        guestSetupCompleted={Boolean(currentTask?.guest_setup_completed)}
                        guestStagingLineCount={currentTask?.checklist_guest_amenity_staging?.length ?? 0}
                        propertySubtitle={inventoryPageSubtitle}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {currentTask && cleaner?.id && canReportUnitIssue ? (
        <TurnoverUnitIssueReportDialog
          open={unitIssueReportOpen}
          onOpenChange={setUnitIssueReportOpen}
          propertyId={effectivePropertyId!}
          cleaningTaskId={currentTask.id}
          cleanerId={cleaner.id}
          checklistId={currentTask.checklist_id}
          checklistName={displayChecklistName || currentTask.checklist_name}
          tDynamic={tDynamic}
        />
      ) : null}

      {/* Message Thread Modal */}
      {currentTask && (
        <MessageThreadModal
          open={messagesOpen}
          onOpenChange={setMessagesOpen}
          taskId={currentTask.id}
          taskTitle={tDynamic("Task Messages")}
        />
      )}

      {/* Restart Cleaning Confirmation */}
      <AlertDialog open={showRestartConfirm} onOpenChange={setShowRestartConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tDynamic("Restart Cleaning?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tDynamic("This will clear all your progress and reset the checklist to 0%. Are you sure?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tDynamic("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestartCleaning}>
              {tDynamic("Restart")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AppLayout>
  );
};

export default CleanerPortal;
